import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Shield, Database, Eye, UserCheck, Clock, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Privacy Policy - 21 Days Of Habit | Gaurav Gadhari";
  }, []);

  return (
    <PageTransition>
      <div className="bg-black text-white noise-bg">
        <header className="relative">
          <Navbar />
        </header>

        <main className="min-h-screen pt-20">
          {/* Header Section */}
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-4xl mx-auto">
              {/* Back Button */}
              <div className="mb-8">
                <Button
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-white hover:bg-white/10 border border-white/20 px-4 py-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </div>

              {/* Main Header */}
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="border border-white/20 p-4 bg-white/5">
                    <Smartphone className="h-12 w-12 text-white" />
                  </div>
                </div>
                <h1 className="font-display text-6xl md:text-7xl tracking-tighter mb-4 opacity-0 animate-fade-in">
                  PRIVACY POLICY
                </h1>
                <p className="text-xl text-neutral-300 mb-6 opacity-0 animate-fade-in" style={{animationDelay: '200ms'}}>
                  21 Days Of Habit Mobile Application
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-neutral-400 opacity-0 animate-fade-in" style={{animationDelay: '400ms'}}>
                  <Clock className="h-4 w-4" />
                  Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>

              <div className="space-y-8">
                {/* Introduction */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '600ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <Eye className="h-6 w-6 text-white" />
                    INTRODUCTION
                  </h2>
                  <div className="space-y-4 text-neutral-300 leading-relaxed">
                    <p>
                      This privacy policy describes how <strong className="text-white">"21 Days Of Habit"</strong> (the "App") collects, uses, and protects your information when you use our mobile application. The App is developed and maintained by <strong className="text-white">Gaurav Gadhari</strong> ("we," "our," or "us").
                    </p>
                    <p>
                      We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This policy explains what information we collect, how we use it, and your rights regarding that information.
                    </p>
                  </div>
                </div>

                {/* Information We Collect */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '800ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <Database className="h-6 w-6 text-white" />
                    INFORMATION WE COLLECT
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-white mb-3 text-lg">Information You Provide</h3>
                      <ul className="space-y-3 text-neutral-300">
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Habit Data:</strong> Information about your habits, including habit names, descriptions, schedules, and completion status</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">App Preferences:</strong> Your app settings, theme preferences, notification settings, and customization choices</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Usage Statistics:</strong> Information about how you interact with the app to improve user experience</span>
                        </li>
                      </ul>
                    </div>

                    <div className="border-t border-white/10 pt-6">
                      <h3 className="font-semibold text-white mb-3 text-lg">Automatically Collected Information</h3>
                      <ul className="space-y-3 text-neutral-300">
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Device Information:</strong> Device model, operating system version, and app version for compatibility purposes</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">App Analytics:</strong> Anonymous usage patterns to improve app functionality and user experience</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* How We Use Information */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '1000ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <UserCheck className="h-6 w-6 text-white" />
                    HOW WE USE YOUR INFORMATION
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-white mb-3 text-lg">Primary Uses</h3>
                      <ul className="space-y-2 text-neutral-300">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Provide habit tracking functionality</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Send notifications and reminders</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Generate progress reports and statistics</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Maintain your app preferences</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-3 text-lg">Improvement Uses</h3>
                      <ul className="space-y-2 text-neutral-300">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Improve app performance and stability</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Develop new features and enhancements</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Analyze usage patterns (anonymized)</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span>Fix bugs and technical issues</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Data Storage */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '1200ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <Shield className="h-6 w-6 text-white" />
                    DATA STORAGE & SECURITY
                  </h2>
                  <div className="space-y-6">
                    <div className="border border-white/20 bg-white/5 p-4">
                      <h3 className="font-semibold text-white mb-2">Local Storage Priority</h3>
                      <p className="text-neutral-300 text-sm">
                        Your habit data is primarily stored locally on your device. This ensures your privacy and allows the app to work offline.
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-white mb-2">Security Measures</h4>
                        <ul className="space-y-1 text-sm text-neutral-300">
                          <li>• Data encryption in transit and at rest</li>
                          <li>• Secure local storage implementation</li>
                          <li>• Regular security updates and patches</li>
                          <li>• No unauthorized data transmission</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Data Retention</h4>
                        <ul className="space-y-1 text-sm text-neutral-300">
                          <li>• Data retained as long as app is installed</li>
                          <li>• Data deleted when app is uninstalled</li>
                          <li>• User can manually clear data anytime</li>
                          <li>• No server-side data persistence</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Data Sharing */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '1400ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <Shield className="h-6 w-6 text-white" />
                    DATA SHARING POLICY
                  </h2>
                  <div className="border border-white/20 bg-white/5 p-6">
                    <div className="text-center">
                      <Shield className="h-12 w-12 text-white mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-white mb-2">
                        We Do Not Share Your Personal Data
                      </h3>
                      <p className="text-neutral-300">
                        Your habit data, personal information, and app usage patterns are never sold, shared, or transmitted to third parties for marketing, advertising, or any other commercial purposes.
                      </p>
                    </div>
                    
                    <div className="border-t border-white/10 mt-4 pt-4">
                      <h4 className="font-semibold text-white mb-2">Limited Exceptions:</h4>
                      <ul className="space-y-1 text-sm text-neutral-300">
                        <li>• Anonymous, aggregated usage statistics for app improvement</li>
                        <li>• Technical data required for app functionality (crash reports, etc.)</li>
                        <li>• Legal compliance when required by law</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Your Rights */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '1600ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <UserCheck className="h-6 w-6 text-white" />
                    YOUR RIGHTS & CHOICES
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-white mb-3 text-lg">Data Control Rights</h3>
                      <ul className="space-y-2 text-neutral-300">
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Access:</strong> View all data stored in the app</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Export:</strong> Export your habit data for personal use</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Delete:</strong> Clear all app data at any time</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Modify:</strong> Edit or update your information</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-3 text-lg">App Permissions</h3>
                      <ul className="space-y-2 text-neutral-300">
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Notifications:</strong> For habit reminders (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Storage:</strong> To save your habit data locally</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span><strong className="text-white">Internet:</strong> For ads and optional features</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '1800ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4 flex items-center gap-3">
                    <Mail className="h-6 w-6 text-white" />
                    CONTACT US
                  </h2>
                  <div className="border border-white/20 bg-white/5 p-6">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Questions About This Privacy Policy?
                      </h3>
                      <p className="text-neutral-300 text-sm">
                        We're here to help. Contact us with any privacy-related questions or concerns.
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-white" />
                        <div>
                          <p className="font-medium text-white">Email</p>
                          <a 
                            href="mailto:gauravgadhari39@gmail.com" 
                            className="text-neutral-300 hover:text-white transition-colors"
                          >
                            gauravgadhari39@gmail.com
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Smartphone className="h-5 w-5 text-white" />
                        <div>
                          <p className="font-medium text-white">Developer</p>
                          <p className="text-neutral-300">Gaurav Gadhari</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Updates */}
                <div className="border border-white/10 bg-white/5 p-6 opacity-0 animate-fade-in" style={{animationDelay: '2000ms'}}>
                  <h2 className="font-display text-2xl tracking-wider mb-4">POLICY UPDATES</h2>
                  <p className="text-neutral-300 text-sm leading-relaxed mb-4">
                    We may update this privacy policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify users of any material changes through the app or via email. Your continued use of the app after such modifications constitutes your acceptance of the updated privacy policy.
                  </p>
                  <div className="p-3 border border-white/10 bg-white/5">
                    <p className="text-xs text-neutral-400">
                      <strong className="text-white">Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center text-sm text-neutral-400 py-8 border-t border-white/10">
                  <p>© 2025 Gaurav Gadhari. All rights reserved.</p>
                  <p className="mt-1">21 Days Of Habit - Building Better Habits, One Day at a Time</p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default PrivacyPolicy;
